import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useHistory, useLocation } from "react-router-dom";
import axios from "axios";
import AuthService from "../../services/auth";
import AdminSimulateService from "../../services/admin/simulate";
import { useToast } from "@chakra-ui/react";
import ErrorMessage from "../../components/custom/ErrorMessage";

const authService = new AuthService();
const simulateService = new AdminSimulateService();

const SIMULATION_KEY = '@simulation';

const isTokenExpired = (token) => {
    try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return Date.now() >= payload.exp * 1000;
    } catch {
        return true;
    }
};

function useQuery() {
    const { search } = useLocation();
    return useMemo(() => new URLSearchParams(search), [search]);
}

export default function useAuth()
{
    const history = useHistory();
    const toast = useToast();
    const [authenticated, setAuthenticated] = useState(false);
    const [loading, setLoading] = useState(true);
    const [authUser, setAuthUser] = useState(null);
    const [authCompany, setAuthCompany] = useState(null);
    const [authCompanyConfig, setAuthCompanyConfig] = useState(null);
    const [authRole, setAuthRole] = useState(null);
    const [authOwnPermissions, setAuthOwnPermissions] = useState([]);
    const [authIsSuperAdmin, setAuthIsSuperAdmin] = useState(false);
    const [authSimulating, setAuthSimulating] = useState(false);

    const authSimulatingRef = useRef(false);
    authSimulatingRef.current = authSimulating;

    const query = useQuery();

    const restoreSimulation = () => {
        try {
            const raw = localStorage.getItem(SIMULATION_KEY);
            if (!raw) return false;
            const simulationData = JSON.parse(raw);
            if (isTokenExpired(simulationData.access_token)) {
                localStorage.removeItem(SIMULATION_KEY);
                return false;
            }
            axios.defaults.headers.Authorization = `Bearer ${simulationData.access_token}`;
            setAuthCompany({ ...simulationData.company });
            setAuthRole({ ...simulationData.role });
            setAuthCompanyConfig(simulationData.config);
            setAuthOwnPermissions([]);
            setAuthIsSuperAdmin(false);
            setAuthSimulating(true);
            return true;
        } catch {
            localStorage.removeItem(SIMULATION_KEY);
            return false;
        }
    };

    useEffect(() => {
        const authData = authService.authDataFromLocalStorage();
        authRefresh(authData);
        if (authData) restoreSimulation();
        setLoading(false);
    }, []);

    useEffect(() => {
        const interceptor = axios.interceptors.response.use(
            response => response,
            error => {
                if (error.response?.status === 401 && authSimulatingRef.current) {
                    stopSimulation();
                    toast({
                        title: 'Simulação encerrada',
                        description: 'O token do cargo simulado expirou.',
                        position: 'bottom-right',
                        status: 'warning',
                    });
                }
                return Promise.reject(error);
            }
        );
        return () => axios.interceptors.response.eject(interceptor);
    }, []);

    const authRefresh = (data, setLocalStorage = false) => {

        if(setLocalStorage === true)
        {
            authService.setAuthDataFromLocalStorage(data);
        }

        setAuthSimulating(false);

        if(data)
        {
            axios.defaults.headers.Authorization = `Bearer ${data.access_token}`;

            setAuthenticated(true);

            setAuthUser(data.user);

            setAuthCompany(data?.company || null);

            setAuthRole(data?.role || null);

            setAuthCompanyConfig(data?.config || null);

            const ownPermissions = data.user?.own_permissions || [];

            setAuthOwnPermissions(ownPermissions);

            setAuthIsSuperAdmin(ownPermissions.includes('USER_IS_SUPER_ADMIN'));
        }
        else
        {
            setAuthUser(null);

            setAuthCompany(null);

            setAuthRole(null);

            setAuthCompanyConfig(null);

            setAuthOwnPermissions([]);

            setAuthIsSuperAdmin(false);
        }
    };

    const handleLogin = (credentials) => {

        setLoading(true);

        authService.login(credentials)
                        .then(({data}) => {

                            authRefresh(data, true);

                            history.push(query.get("redirect") || '/home');

                        })
                        .catch(error => {

                              toast({
                                  title: 'Autenticar',
                                  description: error?.data?.message ||  "Erro ao tentar autenticar",
                                  position: 'bottom-right',
                                  status: 'error',
                              });
                        })
                        .finally(() => {

                            setLoading(false);
                        });
    }

    const handleMailPinCodeLogin = (email, pin) => {

        setLoading(true);

        return authService.verifyMailPinCode(email, pin)
                        .then(({data}) => {

                            authRefresh(data, true);

                            history.push(query.get("redirect") || '/home');

                        })
                        .catch(error => {

                              toast({
                                  title: 'Autenticar',
                                  description: error?.data?.message ||  "Código PIN inválido ou expirado",
                                  position: 'bottom-right',
                                  status: 'error',
                              });
                        })
                        .finally(() => {

                            setLoading(false);
                        });
    }

    function handleLogout(redirect:? string = null)
    {
        setAuthenticated(false);

        setAuthUser(null);

        setAuthCompany(null);

        setAuthRole(null);

        setAuthCompanyConfig(null);

        setAuthSimulating(false);

        localStorage.removeItem(SIMULATION_KEY);

        authService.removeAuthDataFromLocalStorage();

        axios.defaults.headers.Authorization = undefined;

        history.push({
            pathname: '/auth/login',
            search: typeof redirect === 'string' ? "?" + new URLSearchParams({redirect: redirect}).toString() : null
        });
    }

    const startSimulation = (roleOption) => {
        return simulateService
            .simulateRole(roleOption.label)
            .then(({ data }) => {
                localStorage.setItem(SIMULATION_KEY, JSON.stringify({
                    access_token: data.access_token,
                    company: data.company,
                    role: data.role,
                    config: data.config,
                }));
                axios.defaults.headers.Authorization = `Bearer ${data.access_token}`;
                setAuthCompany({ ...data.company });
                setAuthRole({ ...data.role });
                setAuthCompanyConfig(data.config);
                setAuthOwnPermissions([]);
                setAuthIsSuperAdmin(false);
                setAuthSimulating(true);
            })
            .catch(error => {
                toast({
                    title: 'Simular Cargo',
                    description: <ErrorMessage error={error?.data} />,
                    position: 'bottom-right',
                    status: 'error',
                });
            })
            .finally(() => {});
    };

    const stopSimulation = () => {
        localStorage.removeItem(SIMULATION_KEY);
        authRefresh(authService.authDataFromLocalStorage());
    };

    function authHasAnyPermission(has: Array<string>, needs: Array<string>): boolean {

        if(has.includes('*'))
            return true;

        if(!needs.length)
            return true;

        // Permissões próprias do usuário (independentes do contexto de empresa)
        if(needs.some(need => authOwnPermissions.includes(need)))
            return true;

        // Permissões do cargo/role
        return needs.some(need => has.includes(need));
    }

    return { authenticated, loading, handleLogin, handleMailPinCodeLogin, handleLogout, authUser, authCompany, authRole, authCompanyConfig, authOwnPermissions, authIsSuperAdmin, authSimulating, startSimulation, stopSimulation, authRefresh, authHasAnyPermission };
}
